import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'

import { fetchVatDeclarationById } from '@features/vatDeclarations/query-api'
import { Correction } from '@features/vatDeclarations/types/correction'
import { VatDeclarationsPeriod } from '@features/vatDeclarations/types/vatDeclarationsPeriod'
import { VatDeclarationsReport } from '@features/vatDeclarations/types/vatDeclarationsReport'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { useClosableBanner } from '../../../hooks/useClosableBanner'
import { SubmitStatus } from '../elements/VatDeclaration/enums/submitStatus'
import { VatDeclarationStatus } from '../enums/vatDeclarationStatus'
import { useVatDeclarationSubmissions } from '../hooks/useVatDeclarationSubmissions'
import { VatDeclarationSubmission } from '../types/vatDeclarationSubmission'
import { getPeriodData, PeriodData } from '../utils/getPeriodData'
import { getVatDeclarationStatus } from '../utils/getVatDeclarationStatus'

interface VatDeclarationContextState {
  closeSubmissionFailedBanner: () => void
  corrections?: Correction[]
  currentCorrection?: Correction
  endDate: string
  id: string
  isAdvancedSettlement?: boolean
  isLoading: boolean
  isPeriodPaid?: boolean
  isPeriodPayable?: boolean
  isPeriodSettled?: boolean
  isSubmissionFailed?: boolean
  isSubmissionFailedBannerClosed: boolean
  paymentDate?: string
  periodData?: PeriodData
  periodText: string
  periodType?: string
  report?: VatDeclarationsReport
  skatTransactionId?: string
  submission?: VatDeclarationSubmission
  startDate: string
  status: VatDeclarationStatus
  totalAmount: number
  vatDeclaration?: VatDeclarationsPeriod
}

const VatDeclarationContext = createContext<VatDeclarationContextState | undefined>(undefined)

interface VatDeclarationContextProps {
  children?: ReactNode
  vatDeclarationId: string
}

export const VatDeclarationContextProvider = ({
  children,
  vatDeclarationId,
}: VatDeclarationContextProps): ReactElement | null => {
  const localStorageKey = `${LocalStorageKeys.VatDeclarationsSubmissionFailedBanner}-${vatDeclarationId}`
  const { isClosed: isSubmissionFailedBannerClosed, close: closeSubmissionFailedBanner } =
    useClosableBanner(localStorageKey)

  const { submissionsMap } = useVatDeclarationSubmissions({ ids: vatDeclarationId })
  const submission = submissionsMap?.[vatDeclarationId] || undefined

  const { data: vatDeclaration, isLoading: isLoadingVatDeclarationById } = useQuery(
    [QueryKeys.VatDeclarations, vatDeclarationId],
    () => fetchVatDeclarationById(vatDeclarationId),
    {
      enabled: !!vatDeclarationId,
    },
  )

  const {
    corrections = [],
    endDate = '',
    id = '',
    isAdvancedSettlement = false,
    isPaid = false,
    isPayable = false,
    isSettled = false,
    paymentDate,
    period = '',
    periodText = '',
    periodType,
    report,
    reportDeadline,
    skatTransactionId,
    startDate = '',
    totalAmount = 0,
  } = vatDeclaration?.salesTaxReturn || {}

  const status = useMemo(
    () =>
      getVatDeclarationStatus({
        isPaid,
        isPayable,
        isSettled,
        periodEndDate: endDate,
        submissionStatus: submission?.status,
        reportDeadline,
      }),
    [endDate, isPaid, isPayable, isSettled, reportDeadline, submission?.status],
  )

  return (
    <VatDeclarationContext.Provider
      value={{
        closeSubmissionFailedBanner,
        corrections,
        currentCorrection: corrections.find(({ salesTaxReturnId }) => salesTaxReturnId === id),
        endDate,
        id,
        isAdvancedSettlement,
        isLoading: isLoadingVatDeclarationById,
        isPeriodPaid: isPaid,
        isPeriodPayable: isPayable,
        isPeriodSettled: isSettled,
        isSubmissionFailed: submission?.status === SubmitStatus.Failed,
        isSubmissionFailedBannerClosed,
        paymentDate,
        periodData: getPeriodData(period),
        periodText,
        periodType,
        report,
        submission,
        startDate,
        status,
        skatTransactionId,
        totalAmount,
        vatDeclaration: vatDeclaration?.salesTaxReturn,
      }}
    >
      {children}
    </VatDeclarationContext.Provider>
  )
}

export const useVatDeclaration = () => {
  const context = useContext(VatDeclarationContext)

  if (!context) {
    throw new Error('VatDeclarationContextProvider is missing in the module!')
  }

  return context
}
